import { Link } from 'react-router-dom'
import type { AuthorRef } from '@/api/types'
import { cn } from '@/lib/utils'
import { AvatarImage } from './avatar-image'

/**
 * Standard "by @handle" attribution chip. Replaces every place that previously rendered a raw
 * OIDC subject string in Phase 1–3. The display name is the headline; the @handle is the link
 * target so the same chip works on hover-tooltips and small footer text.
 *
 * <p>Phase 7: a 24×24 Avatar slot renders to the left of the handle text. Falls back to a
 * Default Avatar (initials-on-color) when {@code author.avatarUrl} is null.
 */
interface HandleChipProps {
  author: AuthorRef
  showDisplayName?: boolean
  /** Hide the avatar entirely. Useful for tight inline contexts ("By @bob") that don't want chrome. */
  hideAvatar?: boolean
  className?: string
}

export const HandleChip = ({
  author,
  showDisplayName = true,
  hideAvatar = false,
  className,
}: HandleChipProps) => (
  <Link
    to={`/u/${author.handle}`}
    className={cn('inline-flex items-center gap-1.5 hover:underline', className)}
  >
    {!hideAvatar && (
      <AvatarImage
        avatarUrl={author.avatarUrl}
        handle={author.handle}
        displayName={author.displayName}
        size={24}
      />
    )}
    {showDisplayName && <span>{author.displayName}</span>}
    <span className="text-muted-foreground">@{author.handle}</span>
  </Link>
)
